/* eslint-disable react/jsx-no-bind */
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuPortal,
} from "~/components/shared/DropdownMenu";
import Icon from "~/components/shared/Icon";
import Badge from "~/components/shared/Badge";
import {
  useFilterStore,
  FilterByOption,
  useStateCounts,
} from "~/components/build/Show/components/Sidebar/useFilterStore";
import classNames from "classnames";
import { useTrack } from "~/components/build/Show/lib/useTrack";
import Count from "~/components/build/Show/components/Count";

// Note: ordering intentional from most to least important
// Skipped is excluded from this list so it won't appear in the filter dropdown
const stateFilterLabel: Record<Exclude<FilterByOption, FilterByOption.Ignored>, string> = {
  [FilterByOption.Failed]: "Failed",
  [FilterByOption.Running]: "Running",
  [FilterByOption.Scheduled]: "Scheduled",
  [FilterByOption.Waiting]: "Waiting",
  [FilterByOption.SoftFailed]: "Soft failed",
  [FilterByOption.Passed]: "Passed",
  [FilterByOption.Canceled]: "Canceled",
  // [FilterByOption.Ignored]: "Skipped", // Intentionally excluded - shown in the sidebar but not filterable
};

export default function FilterByDropdown() {
  const filterBy = useFilterStore((state) => state.filterBy);
  const setFilterBy = useFilterStore((state) => state.setFilterBy);
  const resetFilters = useFilterStore((state) => state.resetFilters);
  const track = useTrack();
  const counts = useStateCounts();

  function toggleFilterBy(option: FilterByOption) {
    let newFilterBy: FilterByOption[] = [];

    if (filterBy.includes(option)) {
      // If the filter is already applied, remove it
      newFilterBy = filterBy.filter((existingOption) => existingOption !== option);
    } else {
      // If the filter is not applied, add it
      newFilterBy = [...filterBy, option];
    }

    setFilterBy(newFilterBy);

    track("Change State Filter", {
      enabled: newFilterBy,
    });
  }

  function disableAutomaticClose(event: Event) {
    event.preventDefault();
  }

  return (
    <DropdownMenu>
      <div className="flex">
        <DropdownMenuTrigger asChild={true}>
          <button
            data-testid="filter-by-dropdown"
            className={classNames(
              "text-charcoal-700 inline-flex max-w-full items-center space-x-1 rounded-md px-2 py-1 text-sm transition-colors hover:bg-purple-100 hover:text-purple-600 focus:z-10",
              {
                "bg-purple-100 text-purple-600": filterBy.length > 0,
                "rounded-r-none": filterBy.length > 0,
              },
            )}
          >
            <Icon icon="heroicons/outline/funnel" className="h-3 w-3 shrink-0" />

            <div className="flex max-w-full items-center gap-1 truncate">
              {filterBy.length === 0 && "Filter"}
              {filterBy.length === 1 && (
                <>
                  State is <span className="font-medium">{stateFilterLabel[filterBy[0]]}</span>
                </>
              )}
              {filterBy.length > 1 && (
                <>
                  States{" "}
                  <Badge outline={true} className="ml-0 border-current leading-normal">
                    {filterBy.length}
                  </Badge>
                </>
              )}
            </div>

            {filterBy.length === 0 && (
              <Icon icon="down-triangle" className="flex-none" style={{ width: 7, height: 7 }} />
            )}
          </button>
        </DropdownMenuTrigger>

        {filterBy.length > 0 && (
          <button
            aria-label="Reset filters"
            onClick={resetFilters}
            className="no-title-tooltip text-charcoal-700 flex w-7 items-center justify-center self-stretch rounded-md rounded-l-none border-l-[1px] border-white bg-purple-100 hover:bg-purple-100 hover:bg-opacity-75 hover:text-purple-600"
          >
            <Icon icon="heroicons/16/solid/x-mark" className="h-4 w-4" />
          </button>
        )}
      </div>

      <DropdownMenuPortal>
        <DropdownMenuContent
          className="space-y-0.5"
          align="start"
          alignOffset={-1}
          data-testid="filter-by-dropdown-content"
        >
          {Object.keys(stateFilterLabel).map((filter) => {
            const isChecked = filterBy.includes(filter as FilterByOption);

            return (
              <DropdownMenuCheckboxItem
                id={filter}
                key={filter}
                onCheckedChange={() => toggleFilterBy(filter as FilterByOption)}
                checked={isChecked}
                onSelect={disableAutomaticClose}
              >
                <div className="flex min-w-0 flex-auto items-center justify-between font-medium">
                  {stateFilterLabel[filter]}

                  {counts[filter] > 0 && <Count>{counts[filter]}</Count>}
                </div>
              </DropdownMenuCheckboxItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}
